import { defineStore } from 'pinia';
import { ref, watch } from 'vue';
import tryParsingJson from '@/shared/utils/try-parsing-json';

/**
 * Operator-level developer flags.
 *
 * State persists across user sessions by design: an operator locks
 * printing or hides the splash screen, and the kiosk keeps that setting
 * across reboots. The persistence contract is **a separate localStorage
 * entry per field**, with the JSON-encoded value, keyed
 * `kiosk_developer_<field>`. This format is what production kiosks have
 * already written, and changing the layout would silently wipe
 * operator state on the deploy that ships the change.
 *
 * `forceKioskMode` and `printLockUuid` are the only string-valued flags
 * (the rest are booleans). `null` means "unset".
 */

const STORAGE_KEY_PREFIX = 'kiosk_developer_';

function load<T>(field: string, fallback: T): T {
  const raw = localStorage.getItem(`${STORAGE_KEY_PREFIX}${field}`);
  return (
    (tryParsingJson(raw, 'developer_settings') as T | null | undefined) ??
    fallback
  );
}

function persist(field: string, value: unknown): void {
  try {
    localStorage.setItem(
      `${STORAGE_KEY_PREFIX}${field}`,
      JSON.stringify(value)
    );
  } catch (_e) {
    // QuotaExceededError or SecurityError — in-memory state already updated
  }
}

export const useDeveloperStore = defineStore('developer', () => {
  const forceKioskMode = ref<string | null>(load('forceKioskMode', null));
  const hideSplashscreen = ref<boolean>(load('hideSplashscreen', false));
  const hideIdlePopup = ref<boolean>(load('hideIdlePopup', false));
  const isPrintLockActive = ref<boolean>(load('isPrintLockActive', false));
  const printLockUuid = ref<string | null>(load('printLockUuid', null));

  // One watcher per field so each writes its own localStorage key,
  // matching the legacy KioskDesignWrapper._saveDeveloperSetting layout.
  // A single $subscribe with a diff would have to know the old value to
  // tell which field changed — per-ref watch sidesteps that.
  watch(forceKioskMode, (v) => persist('forceKioskMode', v));
  watch(hideSplashscreen, (v) => persist('hideSplashscreen', v));
  watch(hideIdlePopup, (v) => persist('hideIdlePopup', v));
  watch(isPrintLockActive, (v) => persist('isPrintLockActive', v));
  watch(printLockUuid, (v) => persist('printLockUuid', v));

  return {
    forceKioskMode,
    hideSplashscreen,
    hideIdlePopup,
    isPrintLockActive,
    printLockUuid,
  };
});
